import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { HeadphonesIcon, Plus, Copy, Pencil, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AdminPage } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";

const genTempPassword = () => "Belo!" + Math.random().toString(36).slice(-8).toUpperCase();

const AdminBdc = () => {
  const [rows, setRows] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [tempPasswordShown, setTempPasswordShown] = useState(null);
  const [form, setForm] = useState({ email: "", full_name: "", phone: "", temporary_password: "" });
  const [editingAgent, setEditingAgent] = useState(null);
  const [editForm, setEditForm] = useState({ email: "", full_name: "", phone: "" });

  const reload = () => api.get("/admin/bdc-agents").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { reload(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const tp = form.temporary_password || genTempPassword();
    try {
      await api.post("/admin/bdc-agents", { ...form, temporary_password: tp });
      setTempPasswordShown({ email: form.email, password: tp });
      toast.success("BDC agent created");
      setAddOpen(false);
      setForm({ email: "", full_name: "", phone: "", temporary_password: "" });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (u, next) => {
    try {
      await api.patch(`/admin/bdc-agents/${u.user_id}`, { is_active: next });
      toast.success(next ? "Agent reactivated" : `Agent deactivated${u.active_lead_count ? ` · ${u.active_lead_count} leads released to queue` : ""}`);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.full_name}? They will be forced to change it on next login.`)) return;
    try {
      const { data } = await api.post(`/admin/bdc-agents/${u.user_id}/reset-password`, {});
      setTempPasswordShown({ email: u.email, password: data.temp_password });
      toast.success("Temporary password generated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (u) => {
    setEditingAgent(u);
    setEditForm({ email: u.email, full_name: u.full_name, phone: u.phone || "" });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editingAgent) return;
    try {
      await api.patch(`/admin/bdc-agents/${editingAgent.user_id}`, {
        email: editForm.email,
        full_name: editForm.full_name,
        phone: editForm.phone || undefined,
      });
      toast.success("Agent updated");
      setEditingAgent(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminPage
      title="BDC team"
      subtitle="Manage the concierge team that handles customer follow-ups platform-wide."
      testId="admin-bdc-page"
      action={<Button className="bg-brand hover:bg-brand-hover text-white" onClick={() => setAddOpen(true)} data-testid="admin-add-bdc-btn"><Plus size={14} /> Add BDC agent</Button>}
    >
      {!rows ? <div className="h-64 bg-slate-100 rounded-2xl animate-pulse" /> :
        rows.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center">
            <HeadphonesIcon size={28} className="mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500 text-sm">No BDC agents yet.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="admin-bdc-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Agent</th>
                  <th className="text-right px-4 py-3 font-semibold">Active leads</th>
                  <th className="text-right px-4 py-3 font-semibold">Chats sent</th>
                  <th className="text-right px-4 py-3 font-semibold">Appts booked (30d)</th>
                  <th className="text-right px-4 py-3 font-semibold">Showed up (30d)</th>
                  <th className="text-right px-4 py-3 font-semibold">Avg response</th>
                  <th className="text-center px-4 py-3 font-semibold">Active</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.user_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-bdc-row-${u.user_id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium">{u.full_name}</p>
                      <p className="text-[11px] text-slate-500">{u.email}</p>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{u.active_lead_count}</td>
                    <td className="px-4 py-3 text-right">{u.chats_sent}</td>
                    <td className="px-4 py-3 text-right">{u.appts_booked}</td>
                    <td className="px-4 py-3 text-right text-emerald-700 font-medium">{u.showed_up_count}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{u.avg_response_time_minutes != null ? `${u.avg_response_time_minutes.toFixed(1)}m` : "—"}</td>
                    <td className="px-4 py-3 text-center">
                      <Switch checked={u.is_active !== false} onCheckedChange={(v) => toggle(u, v)} data-testid={`admin-bdc-toggle-${u.user_id}`} />
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openEdit(u)} data-testid={`admin-bdc-edit-${u.user_id}`}>
                        <Pencil size={12} /> Edit
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-amber-700 hover:text-amber-800" onClick={() => resetPassword(u)} data-testid={`admin-bdc-reset-${u.user_id}`}>
                        <KeyRound size={12} /> Reset PW
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <Dialog open={!!editingAgent} onOpenChange={(open) => !open && setEditingAgent(null)}>
        <DialogContent data-testid="admin-edit-bdc-dialog">
          <DialogHeader>
            <DialogTitle>Edit BDC agent</DialogTitle>
            <DialogDescription>Update email, name, or phone. Changes are audited.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-3">
            <div><Label className="text-xs uppercase tracking-widest text-slate-500">Full name</Label><Input required value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} data-testid="admin-edit-bdc-name" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-slate-500">Email</Label><Input required type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="admin-edit-bdc-email" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-slate-500">Phone</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="admin-edit-bdc-phone" /></div>
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
              <Button type="submit" className="bg-brand hover:bg-brand-hover text-white" data-testid="admin-edit-bdc-submit">Save changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="admin-add-bdc-dialog">
          <DialogHeader>
            <DialogTitle>New BDC agent</DialogTitle>
            <DialogDescription>They'll receive a temp password below — share it privately. They'll be forced to change it on first login.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><Label className="text-xs uppercase tracking-widest text-slate-500">Full name</Label><Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="admin-add-bdc-name" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-slate-500">Email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-add-bdc-email" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-slate-500">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Temporary password</Label>
              <div className="flex gap-2">
                <Input value={form.temporary_password} onChange={(e) => setForm({ ...form, temporary_password: e.target.value })} placeholder="Auto-generated if empty" data-testid="admin-add-bdc-password" />
                <Button type="button" variant="outline" onClick={() => setForm({ ...form, temporary_password: genTempPassword() })}>Generate</Button>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
              <Button type="submit" className="bg-brand hover:bg-brand-hover text-white" data-testid="admin-add-bdc-submit">Create agent</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tempPasswordShown} onOpenChange={() => setTempPasswordShown(null)}>
        <DialogContent data-testid="admin-bdc-temp-password-dialog">
          <DialogHeader>
            <DialogTitle>Agent created — copy this now</DialogTitle>
            <DialogDescription>This is the only time the temporary password will be shown.</DialogDescription>
          </DialogHeader>
          {tempPasswordShown && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 font-mono text-sm">
              <p><span className="text-slate-500">Email:</span> {tempPasswordShown.email}</p>
              <p><span className="text-slate-500">Password:</span> <span className="font-bold" data-testid="admin-bdc-temp-password-value">{tempPasswordShown.password}</span></p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => { navigator.clipboard?.writeText(`${tempPasswordShown?.email} / ${tempPasswordShown?.password}`); toast.success("Copied"); }}>
              <Copy size={13} /> Copy
            </Button>
            <DialogClose asChild><Button variant="outline">Close</Button></DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
};

export default AdminBdc;
